import { type Cell, type Row as TanstackRow, flexRender } from '@tanstack/react-table';
import styled from '@emotion/styled';
import type { VirtualItem } from '@tanstack/react-virtual';
import { Warning } from '@phosphor-icons/react';
import { Tooltip } from 'components';
import { useTheme } from '@emotion/react';
import { TableConfig } from '../types';
import { useTableContext } from '../providers/TableProvider';
import { styledTheme } from 'styles/theme/theme';
import { stringParse } from 'utils';
import {
  isCellEditable,
  isTaskRowUnreadForRow,
  resolveTableRowCellBackgroundColor,
} from '../utils';
import { IconButton } from 'components/button';
import { Link } from 'react-router-dom';
import { memo, useMemo } from 'react';

export type RowProps = {
  row: TanstackRow<unknown>;
  rowIndex: number;
  virtualItem: VirtualItem;
  tooltipDelay?: number;
  config: TableConfig;
  tableWidth: number;
  loggedInUserId?: string | number;
  getInvalidRow?: (original: unknown) => boolean | undefined;
};

type RowData = {
  invalid?: boolean;
  viewedOn?: Date | null;
  isTaskUnread: boolean;
};

export const Row = memo(
  ({
    row,
    rowIndex,
    virtualItem,
    tooltipDelay = 1000,
    config,
    tableWidth,
    loggedInUserId,
    getInvalidRow,
  }: RowProps) => {
    const { variant = 'default', expandedRowBackgroundColor, getRowBackground } = useTableContext();
    const isSelected = row?.getIsSelected() ? 'selected' : '';
    const original = row?.original ?? {};
    const theme = useTheme();

    const isTaskUnread = isTaskRowUnreadForRow(row, loggedInUserId);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row.original is untyped (unknown) in shared table component
    const isInvalidRow = (original as RowData)?.invalid ?? getInvalidRow?.(row?.original);
    const isSubRow = row?.depth > 0;

    const cellBackgroundColor = useMemo(
      () =>
        resolveTableRowCellBackgroundColor({
          row,
          isSelected: Boolean(row?.getIsSelected()),
          isTaskUnread,
          isInvalidRow: Boolean(isInvalidRow),
          variant,
          themeBgPrimary: theme.palette.bg.primary,
          expandedRowBackgroundColor,
          getRowBackground,
        }),
      [
        row.getIsSelected(),
        row.getIsExpanded?.(),
        expandedRowBackgroundColor,
        getRowBackground,
        isTaskUnread,
        isInvalidRow,
        variant,
        theme.palette.bg.primary,
      ]
    );

    return (
      <tr
        data-testid={`${config?.tableId}_table_row_${rowIndex}`.toLowerCase()}
        className={isSelected}
        data-row-index={rowIndex}
        style={{
          ...(isSubRow ? { borderTop: 'none' } : {}),
          height: `${virtualItem.size}px`,
          transform: `translateY(${virtualItem.start}px)`,
          zIndex: isSubRow ? 1 : 2,
          minWidth: tableWidth,
        }}
      >
        {row?.getVisibleCells().map((cell, index) => {
          const isFrozen = cell.column.getIsPinned();
          const left = isFrozen ? cell?.column?.getStart('left') : 0;
          const isLastCell = index === row.getVisibleCells()?.length - 1;
          const isEditable = isCellEditable(cell.column.columnDef.meta?.editable, cell);
          const tooltipRender = cell.column.columnDef.tooltipRender;
          const border = cell.column.columnDef.border;
          const cellClick = cell.column.columnDef.meta?.onCellClick;
          const isSelector = cell.column.id === 'Selector';
          const primary = cell.column.columnDef.meta?.primary;

          const tooltipTitle = tooltipRender?.(cell);
          const shouldRenderTooltip = tooltipRender && !isInvalidRow && tooltipTitle;

          const cellContent = (
            <CellRender
              cell={cell}
              config={config}
              index={index}
              invalid={isInvalidRow}
              primary={primary}
            />
          );

          const cellDiv = (
            <CellDiv
              data-testid={`${config?.tableId}_table_cell_${cell?.id}`.toLowerCase()}
              key={cell.id}
              className={`${isFrozen ? 'frozen ' : ''}${isSelector ? 'selector ' : ''}${
                row?.getIsSelected() ? 'selected' : ''
              }${isTaskUnread ? ' unread' : ''}`}
              isEditable={isEditable}
              noPadding={cell.column.columnDef.meta?.noPadding}
              bold={cell.column.columnDef.meta?.bold || Boolean(primary)}
              onClick={() => {
                if (!isSelector && cellClick && !isEditable) {
                  cellClick(cell);
                }
              }}
              isInvalidRow={isInvalidRow}
              style={{
                left,
                width: cell.column.getSize(),
                minWidth: cell.column.getSize(),
                ...(isLastCell ? { flexGrow: 1 } : {}),
                ...(border === 'left' && {
                  borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                }),
                ...(border === 'right' && {
                  borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                }),
                ...(border === 'both' && {
                  borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                  borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                }),
                ...(cellBackgroundColor ? { backgroundColor: cellBackgroundColor } : {}),
                ...(isSelector
                  ? { borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}` }
                  : {}),
              }}
            >
              {cellContent}
            </CellDiv>
          );

          return shouldRenderTooltip ? (
            <Tooltip
              key={cell.id}
              title={tooltipTitle}
              disableFocusListener
              disableHoverListener={false}
              disableInteractive
              enterDelay={tooltipDelay}
              enterNextDelay={tooltipDelay}
            >
              {cellDiv}
            </Tooltip>
          ) : (
            cellDiv
          );
        })}
      </tr>
    );
  }
);

Row.displayName = 'Row';

const CellRender = ({
  invalid,
  index,
  cell,
  config,
  primary,
}: {
  cell: Cell<unknown, unknown>;
  index: number;
  invalid?: boolean;
  config: TableConfig;
  primary?:
    | true
    | {
        icon: React.ReactElement;
        tooltip: string;
        onClick?: () => void;
        link?: string;
        vars?: string[];
      }[];
}) => {
  if (invalid && index === 1) {
    return (
      <Icon>
        <Tooltip title={config?.texts?.invalidRow}>
          <Warning size={24} />
        </Tooltip>{' '}
        <Tooltip title={cell?.column?.columnDef?.tooltipRender?.(cell) ?? ''}>
          <span>{flexRender(cell?.column?.columnDef?.cell, cell?.getContext()) ?? ''}</span>
        </Tooltip>
      </Icon>
    );
  }
  if (Array.isArray(primary)) {
    return (
      <PrimaryButtonContainer>
        {flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}
        {primary.map(p => {
          if (p.link) {
            let url = p.link;
            if (p.vars?.length) {
              const row = cell.row.original as Record<string, unknown>;
              url = stringParse(
                p.link,
                p.vars.map(v => String(row[v] ?? ''))
              );
            }
            return (
              <Link to={url} key={p.tooltip}>
                <IconButton className="primary-buttons">
                  <Tooltip title={p.tooltip}>{p.icon}</Tooltip>
                </IconButton>
              </Link>
            );
          }
          if (p.onClick) {
            return (
              <IconButton className="primary-buttons" key={p.tooltip} onClick={p.onClick}>
                <Tooltip title={p.tooltip}>{p.icon}</Tooltip>
              </IconButton>
            );
          }
          return null;
        })}
      </PrimaryButtonContainer>
    );
  }
  return flexRender(cell?.column?.columnDef?.cell, cell?.getContext());
};

const PrimaryButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  > span:first-of-type {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button {
    display: none;
    flex-shrink: 0;
  }
`;

const Icon = styled.span`
  && {
    gap: 8px;
    display: flex;
    align-items: center;
    > svg {
      flex-shrink: 0;
    }
    > a,
    div,
    span {
      overflow: hidden;
    }
  }
`;

export const CellDiv = styled.td<{
  isEditable?: boolean;
  noPadding?: boolean;
  bold?: boolean;
  isInvalidRow?: boolean;
}>`
  && {
    cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
    ${p => (p.noPadding ? 'padding: 0;' : '')}
    ${p => (p.bold ? `font-weight: ${p.theme.typography.weight.semiBold};` : '')}
  }
  .dotsMenuBtn {
    all: unset;
    cursor: pointer;
  }
  &.selector {
    padding: unset;
  }
  .dotsMenu {
    display: none;
    background-color: transparent;
    padding-top: 1px;
    border-radius: 3px;
    height: 18px;
  }
  & > * {
    // sets the opacity on children of the cell to avoid affecting the border color for invalid row we dont need color : rgba(74, 76, 80, 0.3); only 0.3 opacity and #4a4c50(current color) will make rgba(74, 76, 80, 0.3) color
    opacity: ${p => (p.isInvalidRow ? 0.3 : undefined)};
  }
  :hover {
    .dotsMenu {
      display: flex;
    }
    .primary-buttons {
      display: flex;
    }
  }
`;
